/**
 * TCR-1143 (final review, findings #2/#3) — tổng chiết khấu khuyến mãi ĐÃ
 * LOẠI khoản flash sale, dùng chung cho mọi màn hiển thị "Khuyến mãi: -X".
 *
 * Bối cảnh: dòng hàng mua theo GIÁ FLASH SALE đã mang giá ĐÃ GIẢM SẴN (xem
 * `addFlashSaleItem`, `POS/index.tsx`) — `item.price` đã là giá flash, không
 * phải giá gốc trừ đi chiết khấu. BE (`evaluate()`/`applied_promotions` lưu
 * trên đơn) vẫn trả về một entry `type: 'flash_sale'` với `discount_amount`
 * bằng đúng khoản đã giảm SO VỚI GIÁ GỐC — con số này CHỈ để hiển thị "đã
 * tiết kiệm bao nhiêu", KHÔNG phải một khoản giảm giá CỘNG THÊM cần trừ lần
 * nữa khỏi tổng tiền hàng. Cộng cả khoản này vào tổng "Khuyến mãi" hiển thị
 * sẽ trừ giá flash sale HAI LẦN — một lần ẩn trong `item.price`, một lần ở
 * đây — ra một con số "Khuyến mãi: -X" sai (không ảnh hưởng số tiền khách
 * THỰC TRẢ, vì số đó đọc từ `money`/`summary.total` đã tính đúng ở BE, chỉ
 * sai ở con số hiển thị phụ này).
 *
 * Bug này từng được vá RIÊNG LẺ ở `CustomerSidebar/index.tsx` (2 call site,
 * copy tay cùng 1 đoạn filter+reduce) — cùng bug vẫn còn nguyên ở
 * `Orders/detail.tsx` và `OrderSummaryModal.tsx` (đọc từ
 * `order.summary.applied_promotions`, cùng field, cùng công thức sai). Rút
 * ra hàm chung ở đây để có ĐÚNG MỘT nơi sửa nếu logic đổi, thay vì N bản
 * copy tay dễ lệch nhau theo thời gian.
 */

/** Giá trị `type` của entry flash sale trên `applied_promotions` (BE, promotion-engine.service.ts). */
export const FLASH_SALE_PROMOTION_TYPE = 'flash_sale' as const;

/** Giá trị `applied_to` của khoản giảm rơi vào phí vận chuyển (CTKM freeship). */
export const SHIPPING_APPLIED_TO = 'shipping' as const;

/**
 * Một entry tối thiểu của `applied_promotions[]` — cố ý chỉ đòi hỏi 2 field
 * cần cho phép tính này, để hàm dùng được với CẢ hai shape đang tồn tại
 * trong codebase: `AppliedPromotion` (`promotion.service.ts`, `type: string`
 * bắt buộc) và entry trên `TenantOrder.summary.applied_promotions`
 * (`mocks/orders.ts`, `type?: string` optional — đơn cũ có thể thiếu field
 * này, coi như không phải flash sale, vẫn cộng vào tổng như hành vi cũ).
 */
export interface PromotionDiscountEntry {
  type?: string;
  discount_amount: number;
  /**
   * `'shipping'` ⇒ khoản giảm này rơi vào PHÍ VẬN CHUYỂN, không phải tiền
   * hàng. Optional: entry trên đơn cũ và một số shape không mang field này —
   * thiếu thì coi như khoản giảm tiền hàng, đúng hành vi trước đây.
   */
  applied_to?: string;
}

/**
 * Tổng `discount_amount` của mọi entry KHÔNG PHẢI flash sale. Dùng thay cho
 * `appliedPromotions.reduce((sum, p) => sum + p.discount_amount, 0)` trần ở
 * bất kỳ đâu hiển thị tổng "Khuyến mãi" cho khách/thu ngân xem.
 *
 * KHÔNG dùng hàm này để lọc danh sách CHIP/nhãn khuyến mãi hiển thị riêng lẻ
 * (vd `appliedLabels.promotions` ở `OrderInfoPanel`) — chip vẫn phải hiện cả
 * flash sale để thu ngân biết đơn có áp dụng, chỉ TỔNG đưa vào tính tiền mới
 * cần loại trừ.
 */
export function sumNonFlashPromotionDiscount(
  appliedPromotions: PromotionDiscountEntry[] | undefined | null,
): number {
  if (!appliedPromotions) return 0;
  return appliedPromotions
    .filter((p) => p.type !== FLASH_SALE_PROMOTION_TYPE)
    // TCR-1244: CTKM freeship cũng nằm trong `applied_promotions`, nhưng khoản
    // của nó trừ vào PHÍ VẬN CHUYỂN (đọc riêng ở `shipping_discount`), không
    // phải tiền hàng. Cộng vào đây là trừ HAI LẦN — và tệ hơn flash sale ở chỗ
    // nó còn hạ cơ sở tính thuế của tiền hàng, nên khách được giảm nhiều hơn
    // phí ship đúng phần VAT (đơn thật: phí 30.000 mà số phải thu tụt 33.000).
    .filter((p) => p.applied_to !== SHIPPING_APPLIED_TO)
    .reduce((sum, p) => sum + Number(p.discount_amount ?? 0), 0);
}

/** Nhãn chip một chương trình đã áp, dùng cho `appliedLabels` của `OrderInfoPanel`. */
export interface PromotionLabel {
  name: string;
  amount: number;
}

/**
 * Tách chip khuyến mãi theo TRỤC TIỀN mà nó giảm.
 *
 * Khối tiền xếp chip ngay dưới dòng tổng mà chúng cộng vào, nên chip freeship
 * nằm chung với chip tiền hàng đọc ra mâu thuẫn: "Tổng chiết khấu: -0" mà ngay
 * dưới là một chip "-30.000". Người vận hành nhìn màn này để đối chiếu đơn với
 * chương trình, không có cách nào biết con số đó thuộc phí ship.
 *
 * Chip flash sale VẪN nằm ở nhóm tiền hàng dù không cộng vào tổng — thu ngân
 * cần biết đơn có áp flash sale, và số của nó là "đã tiết kiệm được bao nhiêu"
 * so với giá niêm yết (xem comment đầu file).
 */
export function splitPromotionLabels(
  appliedPromotions: Array<PromotionDiscountEntry & { name?: string }> | undefined | null,
): { goods: PromotionLabel[]; shipping: PromotionLabel[] } {
  const goods: PromotionLabel[] = [];
  const shipping: PromotionLabel[] = [];
  for (const promotion of appliedPromotions ?? []) {
    const label: PromotionLabel = {
      name: promotion.name ?? '',
      amount: Number(promotion.discount_amount ?? 0),
    };
    if (promotion.applied_to === SHIPPING_APPLIED_TO) shipping.push(label);
    else goods.push(label);
  }
  return { goods, shipping };
}
